import type { TIdRef, TUuid } from '../types/common';

export const refId = (ref: TIdRef): TUuid | null => {
  if (!ref) return null;
  return typeof ref === 'string' ? ref : ref.id;
};

export const isNeitherNullNorUndefined = <T>(value: T): value is NonNullable<T> =>
  value !== null && value !== undefined;

export const isTruthy = <T>(value: T): value is Exclude<T, null | undefined | false | 0 | ''> =>
  !!value;

export const compareByName = (
  a: Readonly<{ name: string }>,
  b: Readonly<{ name: string }>,
): number => a.name.localeCompare(b.name, 'de');

/** Each id once, in first-seen order; a later duplicate's value wins. */
export const uniqueById = <T extends Readonly<{ id: TUuid }>>(items: ReadonlyArray<T>): T[] => (
  [...new Map(items.map((item) => [item.id, item])).values()]
);

/** Each group keeps the input order, and the groups come in the order their key first appears. */
export const groupBy = <TKey, TItem>(
  items: ReadonlyArray<TItem>,
  keyOf: (item: TItem) => TKey,
): Map<TKey, TItem[]> => {
  const groups = new Map<TKey, TItem[]>();
  items.forEach((item) => {
    const key = keyOf(item);
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  });
  return groups;
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (value: unknown): boolean => (
  typeof value === 'string' && UUID_PATTERN.test(value)
);

/* A non-empty array of UUIDs. Bulk routes check their `ids` with it because Directus' readByQuery,
   unlike readOne, passes a malformed id on to Postgres. */
export const isUuidList = (value: unknown): value is TUuid[] => (
  Array.isArray(value) && value.length > 0 && value.every(isUuid)
);

export const getErrorMessage = (error: unknown): string => (
  error instanceof Error ? error.message : String(error)
);
